import { useQuery, useSubscription } from '@apollo/client/react';
import { useCallback, useMemo } from 'react';
import {
  GET_ALL_FAMILY_DATA,
  GET_FAMILY_DATA_BY_ID,
  SUBSCRIBE_FAMILY_DATA,
} from '@common/graphql';
import { useFamily } from '../context';
import { insertFamilyData, updateData, deleteFamilyData } from '../services/dataApi';

// Data types supported by the unified family data system
export type FamilyDataType =
  | 'note'
  | 'list'
  | 'allowance'
  | 'expense'
  | 'savings_goal'
  | 'payment'
  | 'photo'
  | 'video'
  | 'quote'
  | 'contact'
  | 'illness'
  | 'allergy'
  | 'medication'
  | 'school'
  | 'class'
  | 'chore'
  | 'routine'
  | 'reward'
  | 'activity'
  | 'points_transaction'
  | 'album'
  | 'document'
  | 'email';

// The data structure stored in family_data table
export interface FamilyDataItem<T = any> {
  id: string;
  family_id: string;
  data: {
    id: string;
    type: FamilyDataType;
    data: T;
  };
  status: string;
  created_at: string;
  modified_at: string;
}

interface UseFamilyDataOptions {
  skip?: boolean;
  useSubscription?: boolean;
}

interface UseFamilyDataResult<T> {
  items: FamilyDataItem<T>[];
  loading: boolean;
  error: any;
  refetch: () => Promise<any>;
  // Insert new data - optionally specify familyId for multi-family support
  insert: (data: T, familyId?: string) => Promise<FamilyDataItem<T> | null>;
  update: (id: string, data: T) => Promise<FamilyDataItem<T> | null>;
  remove: (id: string) => Promise<boolean>;
}

/**
 * Hook to manage family data of a specific type
 * Provides CRUD operations for notes, lists, allowances, expenses, etc.
 */
export function useFamilyData<T = any>(
  type: FamilyDataType,
  options?: UseFamilyDataOptions
): UseFamilyDataResult<T> {
  const { currentFamilyId } = useFamily();
  const useRealtime = options?.useSubscription === true;

  // Use subscription for real-time updates
  // Note: family_id filtering is handled by Hasura permissions
  const {
    data: subscriptionData,
    loading: subLoading,
    error: subError,
  } = useSubscription(SUBSCRIBE_FAMILY_DATA, {
    skip: options?.skip || !useRealtime,
  });

  // Query — always active as a refetchable fallback
  // Note: family_id filtering is handled by Hasura permissions
  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(GET_ALL_FAMILY_DATA, {
    skip: options?.skip,
    fetchPolicy: 'cache-and-network',
  });

  // Prefer subscription data when available
  const data = useRealtime && (subscriptionData as any)?.family_data ? subscriptionData : queryData;
  const loading = useRealtime ? subLoading && queryLoading : queryLoading;
  const error = (data as any)?.family_data ? null : subError || queryError;

  // Filter items by type on client side
  const items = useMemo(() => {
    if (!(data as any)?.family_data) return [];
    return (data as any).family_data.filter(
      (item: FamilyDataItem<T>) => item.data?.type === type
    );
  }, [data, type]);

  // Insert new data - optionally specify familyId for multi-family support
  const insert = useCallback(
    async (itemData: T, familyId?: string): Promise<FamilyDataItem<T> | null> => {
      const targetFamilyId = familyId || currentFamilyId;
      if (!targetFamilyId) {
        console.error('No family found to insert data');
        return null;
      }

      try {
        const result = await insertFamilyData(targetFamilyId, type, itemData);
        return result || null;
      } catch (err) {
        console.error('Error inserting family data:', err);
        throw err;
      }
    },
    [currentFamilyId, type]
  );

  // Update existing data
  const update = useCallback(
    async (id: string, itemData: T): Promise<FamilyDataItem<T> | null> => {
      try {
        const result = await updateData(id, type, itemData);
        return result || null;
      } catch (err) {
        console.error('Error updating family data:', err);
        throw err;
      }
    },
    [type]
  );

  // Soft delete data
  // Note: family_id filtering is handled by Hasura permissions
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await deleteFamilyData(id);
        return true;
      } catch (err) {
        console.error('Error deleting family data:', err);
        throw err;
      }
    },
    []
  );

  return {
    items,
    loading,
    error,
    refetch,
    insert,
    update,
    remove,
  };
}

/**
 * Hook to fetch a single family data item by ID
 */
export function useFamilyDataItem<T = any>(id: string | undefined) {
  const { data, loading, error, refetch } = useQuery(GET_FAMILY_DATA_BY_ID, {
    variables: { id },
    skip: !id,
  });

  return {
    item: (data as any)?.family_data_by_pk as FamilyDataItem<T> | null,
    loading,
    error,
    refetch,
  };
}

export default useFamilyData;
